// v8.8c — Risk Profile selector.
//
// Three preset profiles map to backend (balance_sl_pct, balance_tp_pct,
// min_signal_score) tuples. UI is a 3-segment toggle. Default is the
// middle option ("Balanced") which matches the previous production
// behaviour, so existing accounts see zero behaviour change unless they
// actively change profile.
//
// API contract:
//   GET  /api/bot/risk-profile/:account_id
//        → {current, is_default, profiles: {key: {label, description, params}}}
//   PUT  /api/bot/risk-profile/:account_id  body: {profile: string}
import { useState, useEffect, useCallback } from "react";
import { Shield, Scale, Flame, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const PROFILE_META = {
  conservative: { Icon: Shield, color: "text-emerald-400", bg: "bg-emerald-500/15 border-emerald-500/40" },
  balanced:     { Icon: Scale,  color: "text-vts-blue",   bg: "bg-vts-blue/15 border-vts-blue/40" },
  aggressive:   { Icon: Flame,  color: "text-amber-400",  bg: "bg-amber-500/15 border-amber-500/40" },
};
const PROFILE_ORDER = ["conservative", "balanced", "aggressive"];

export function RiskProfileSelector({ accountId, running }) {
  const [current, setCurrent] = useState("balanced");
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null); // which profile key is being saved

  const fetchProfile = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const res = await api.get(`/bot/risk-profile/${encodeURIComponent(accountId)}`);
      setCurrent(res.data?.current || "balanced");
      setProfiles(res.data?.profiles || {});
    } catch {
      // silent — first load before any bot history
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const selectProfile = useCallback(async (key) => {
    if (!accountId || key === current) return;
    setSaving(key);
    try {
      await api.put(`/bot/risk-profile/${encodeURIComponent(accountId)}`, { profile: key });
      setCurrent(key);
      toast.success(`Risk profile switched to ${profiles[key]?.label || key}`, {
        description: running
          ? "Changes apply to the live runner instantly."
          : "Will apply when you start the bot.",
      });
    } catch (e) {
      toast.error("Failed to switch risk profile", {
        description: e?.response?.data?.detail || e?.message,
      });
    } finally {
      setSaving(null);
    }
  }, [accountId, current, profiles, running]);

  if (loading || PROFILE_ORDER.length === 0) {
    return (
      <div className="mt-4 bg-vts-bg/60 border border-vts-line rounded-md px-4 py-3 text-[10px] font-mono text-vts-silver/40">
        Loading risk profile…
      </div>
    );
  }

  return (
    <div
      data-testid="risk-profile-selector"
      className="mt-4 bg-vts-bg/60 border border-vts-line rounded-md px-4 py-3"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-mono">
          Risk profile
        </div>
        <div
          data-testid="risk-profile-current"
          className="text-[9px] font-mono tabular-nums text-vts-silver/50"
        >
          {profiles[current]?.label || current}
        </div>
      </div>

      {/* 3-segment toggle */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        {PROFILE_ORDER.map((key) => {
          const p = profiles[key];
          const meta = PROFILE_META[key];
          if (!p || !meta) return null;
          const { Icon } = meta;
          const isCurrent = current === key;
          const isSaving = saving === key;
          return (
            <button
              key={key}
              data-testid={`risk-profile-btn-${key}`}
              onClick={() => selectProfile(key)}
              disabled={isCurrent || saving !== null}
              className={`flex flex-col items-center justify-center gap-1 py-2.5 px-2 rounded-md border text-[10px] font-mono transition-all ${
                isCurrent
                  ? `${meta.bg} ${meta.color} cursor-default`
                  : "border-vts-line text-vts-silver/60 hover:text-vts-silver hover:border-vts-silver/40"
              }`}
            >
              {isSaving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Icon className={`w-3.5 h-3.5 ${isCurrent ? meta.color : ""}`} />
              )}
              <span>{p.label}</span>
              {isCurrent && <CheckCircle2 className="w-2.5 h-2.5 absolute top-1 right-1 opacity-60" />}
            </button>
          );
        })}
      </div>

      {/* Active profile details */}
      {profiles[current] && (
        <div
          data-testid={`risk-profile-details-${current}`}
          className="bg-vts-bg/40 rounded px-3 py-2 border border-vts-line/40"
        >
          <p className="text-[10px] font-mono text-vts-silver/70 mb-2">
            {profiles[current].description}
          </p>
          <div className="grid grid-cols-3 gap-2 text-[9px] font-mono">
            <Stat label="Account SL" value={`${profiles[current].balance_sl_pct}%`} />
            <Stat label="Account TP" value={`${profiles[current].balance_tp_pct}%`} />
            <Stat label="Score gate" value={`${profiles[current].min_signal_score}/8`} />
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-vts-silver/40 text-[8px] uppercase">{label}</span>
      <span className="text-vts-silver tabular-nums">{value}</span>
    </div>
  );
}

export default RiskProfileSelector;
